import { useCallback, useEffect, useMemo, useState } from 'react';
import { getInlineString, getText } from 'i18n/intl';
import type { BatchEditOperation } from 'types/batchEditObject.type';
import type { TType } from '@timeedit/types';
import {
  Collapse,
  type CollapseProps,
  Form,
  Modal,
  Select,
  Typography,
} from '@timeedit/ui-components/antd';
import type { DefaultOptionType } from '@timeedit/ui-components/antd/select/index';
import { JobObjectSelection } from '../../hooks/useJobSubmit';
import useShowAssociations from '../../hooks/useShowAssociations';
import BulkEditAvailabilityRelations from './BulkEditAvailabilityRelations';
import BulkEditMembers from './BulkEditMembers';
import BulkEditOptionalRelations from './BulkEditOptionalRelations';
import BulkEditRelations from './BulkEditRelations';
import {
  getBulkEditFieldOptions,
  useBulkEditHasSelection,
  useBulkEditSubmit,
  useBulkEditWarnings,
} from './common';

type Props = {
  setOpen: (openSection: 'common' | false) => void;
  open: boolean;
  selectedObjectsCount?: number;
  objectType: TType | null;
  onSubmitSuccess?: () => void;
  jobObjectSelection: JobObjectSelection;
};

const BulkEditAssociationsModal = ({
  open,
  setOpen,
  selectedObjectsCount,
  objectType,
  onSubmitSuccess,
  jobObjectSelection,
}: Props) => {
  const [sendingBatch, setSendingBatch] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const hasSelection = useBulkEditHasSelection(form);
  const { showMembers, showAvailabilityRelated, showOptionalRelated } =
    useShowAssociations(objectType);

  const associationNameMap = useMemo<Record<string, { name: string }>>(() => {
    const map: Record<string, { name: string }> = {
      relations: {
        name: getText(
          'object_manager.object_table.drawer.associations.relations.title'
        ),
      },
    };
    if (showMembers) {
      map.members = {
        name: getText(
          'object_manager.object_table.drawer.associations.memberships.title'
        ),
      };
    }
    if (showAvailabilityRelated) {
      map.availabilityRelations = {
        name: getText(
          'object_manager.object_table.drawer.associations.availability_related.title'
        ),
      };
    }
    if (showOptionalRelated) {
      map.optionalRelations = {
        name: getText(
          'object_manager.object_table.drawer.associations.optional_related.title'
        ),
      };
    }
    return map;
  }, [showMembers, showAvailabilityRelated, showOptionalRelated]);

  const warnings = useBulkEditWarnings(
    form,
    associationNameMap,
    selectedObjectsCount ?? 0
  );

  const options = useMemo(
    () =>
      getBulkEditFieldOptions(
        [
          'add',
          'keep',
          'findReplace',
          'replaceAll',
          'removeSpecific',
          'removeAll',
        ],
        { mandatory: false, unique: false, multiple: true }
      ),
    []
  );

  const membershipsValue = Form.useWatch('members', form);
  const relationsValue = Form.useWatch('relations', form);
  const availabilityRelationsValue = Form.useWatch(
    'availabilityRelations',
    form
  );
  const optionalRelationsValue = Form.useWatch('optionalRelations', form);

  const buildOperations = useCallback(
    (validationResult: Record<string, any>) =>
      transformFormDataToBatchEditOperation(validationResult),
    []
  );

  const onSubmit = useBulkEditSubmit({
    form,
    setSendingBatch,
    setOpen,
    selection: jobObjectSelection,
    operationType: 'EDIT_MEMBERS_AND_RELATIONS',
    buildOperations,
    successMessage: getText('object_manager.bulk-edit-modal.success-message'),
    onSuccess: onSubmitSuccess,
  });

  const collapsibleAssociations = useMemo<CollapseProps['items']>(() => {
    const associations: CollapseProps['items'] = [];

    if (showMembers) {
      associations.push({
        key: 'members',
        label: (
          <SectionLabel
            title={getText(
              'object_manager.object_table.drawer.associations.memberships.title'
            )}
            formItemName="members"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections}
            options={options}
          />
        ),
        children: (
          <BulkEditMembers
            action={membershipsValue}
            form={form}
            objectType={objectType}
          />
        ),
      });
    }

    // The relations section should always be shown
    associations.push({
      key: 'relations',
      label: (
        <SectionLabel
          title={getText(
            'object_manager.object_table.drawer.associations.relations.title'
          )}
          formItemName="relations"
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
          options={options}
        />
      ),
      children: <BulkEditRelations action={relationsValue} form={form} />,
    });

    if (showAvailabilityRelated) {
      associations.push({
        key: 'availabilityRelations',
        label: (
          <SectionLabel
            title={getText(
              'object_manager.object_table.drawer.associations.availability_related.title'
            )}
            formItemName="availabilityRelations"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections}
            options={options}
          />
        ),
        children: (
          <BulkEditAvailabilityRelations
            type={objectType}
            action={availabilityRelationsValue}
            form={form}
          />
        ),
      });
    }

    if (showOptionalRelated) {
      associations.push({
        key: 'optionalRelations',
        label: (
          <SectionLabel
            title={getText(
              'object_manager.object_table.drawer.associations.optional_related.title'
            )}
            formItemName="optionalRelations"
            expandedSections={expandedSections}
            setExpandedSections={setExpandedSections}
            options={options}
          />
        ),
        children: (
          <BulkEditOptionalRelations
            action={optionalRelationsValue}
            form={form}
          />
        ),
      });
    }

    return associations;
  }, [
    showMembers,
    showAvailabilityRelated,
    showOptionalRelated,
    options,
    membershipsValue,
    relationsValue,
    availabilityRelationsValue,
    optionalRelationsValue,
    form,
    expandedSections,
  ]);

  if (!objectType) {
    return null;
  }

  return (
    <Modal
      open={open}
      okText={getText('object_manager.bulk-edit-modal.okText')}
      onOk={onSubmit}
      cancelText={getText('cancel')}
      onCancel={() => setOpen(false)}
      okButtonProps={{
        loading: sendingBatch,
        disabled: !hasSelection,
      }}
      width="70vw"
      title={
        <div className="bulk-edit-modal-title-wrapper">
          {getText('object_manager.bulk-edit-modal.associations.title')}
          <Typography.Text>
            {getInlineString(
              'object_manager.bulk-edit-modal.subtitle',
              selectedObjectsCount,
              objectType.name
            )}
          </Typography.Text>
        </div>
      }
    >
      <Form form={form} layout="horizontal" requiredMark={false}>
        <Collapse
          activeKey={expandedSections}
          onChange={setExpandedSections}
          collapsible="icon"
          items={collapsibleAssociations}
        />
        {warnings}
      </Form>
    </Modal>
  );
};

const SectionLabel = ({
  title,
  formItemName,
  expandedSections,
  setExpandedSections,
  options,
}: {
  title: string;
  formItemName: string;
  expandedSections: string[];
  setExpandedSections: React.Dispatch<React.SetStateAction<string[]>>;
  options: DefaultOptionType[];
}) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      {title}
      <Form.Item name={formItemName}>
        <Select
          options={options}
          style={{ minWidth: '150px' }}
          defaultValue="keep"
          onChange={(value) => {
            if (value !== 'keep' && !expandedSections.includes(formItemName)) {
              setExpandedSections((prev) => [...prev, formItemName]);
            }
          }}
        />
      </Form.Item>
    </div>
  );
};

const addSection = (
  attribute:
    | 'members'
    | 'relations'
    | 'availabilityRelations'
    | 'optionalRelations',
  formData: Record<string, any>,
  operations: BatchEditOperation[]
) => {
  const action = formData[attribute];

  if (!action || action === 'keep') {
    return;
  }

  if (action === 'add') {
    operations.push({
      category: 'membersAndRelations',
      attribute,
      operation: 'add',
      value: formData[`${attribute}_add`],
    });
  } else if (action === 'findReplace') {
    operations.push({
      category: 'membersAndRelations',
      attribute,
      operation: 'findReplace',
      searchValue: formData[`${attribute}_replace`],
      rule: formData[`${attribute}_rule`] ?? 'matchAll',
      value: formData[`${attribute}_with`],
    });
  } else if (action === 'replaceAll') {
    operations.push({
      category: 'membersAndRelations',
      attribute,
      operation: 'replaceAll',
      value: formData[`${attribute}_replaceAll`],
    });
  } else if (action === 'removeSpecific') {
    operations.push({
      category: 'membersAndRelations',
      attribute,
      operation: 'removeSpecific',
      searchValue: formData[`${attribute}_removeSpecific`],
    });
  } else if (action === 'removeAll') {
    operations.push({
      category: 'membersAndRelations',
      attribute,
      operation: 'removeAll',
    });
  }
};

const transformFormDataToBatchEditOperation = (
  formData: Record<string, any>
): BatchEditOperation[] => {
  const operations: BatchEditOperation[] = [];

  addSection('members', formData, operations);
  addSection('relations', formData, operations);
  addSection('availabilityRelations', formData, operations);
  addSection('optionalRelations', formData, operations);

  return operations;
};

export default BulkEditAssociationsModal;
